import React, { cloneElement, useMemo } from 'react';
import type { ControlledProps } from 'react-medium-image-zoom';
import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import useImageIntentFriction from './useImageIntentFriction';

export type ImageIntentZoomContentComponent = NonNullable<
  ControlledProps['ZoomContent']
>;

// react-medium-image-zoom renders the zoomed <img> in a portal under
// document.body, so it is neither a DOM descendant of the thumbnail's
// [data-image-intent] element (the iOS callout CSS misses it) nor a React
// descendant of the element carrying the friction handlers (no long-press
// timer, no drag block, no report). Passing this as `ZoomContent` renders what
// the library renders by default — the modal image, then the unzoom button —
// with the friction props on the modal image itself, reported under
// `${surface}_zoom`.
//
// The component's identity changes only when the context's values do: a new
// identity remounts the modal image, so it must not change on every render.
export const useImageIntentZoomContent = (
  context: ImageIntentContext,
): ImageIntentZoomContentComponent => {
  const contextKey = JSON.stringify(context);

  return useMemo(() => {
    const parsed = JSON.parse(contextKey) as ImageIntentContext;
    const zoomContext: ImageIntentContext = {
      ...parsed,
      surface: `${parsed.surface}_zoom`,
    };

    const ImageIntentZoomContent: ImageIntentZoomContentComponent = ({
      img,
      buttonUnzoom,
    }) => {
      const { getFrictionProps } = useImageIntentFriction();

      return (
        <>
          {img ? cloneElement(img, getFrictionProps(zoomContext)) : img}
          {buttonUnzoom}
        </>
      );
    };

    return ImageIntentZoomContent;
  }, [contextKey]);
};

export default useImageIntentZoomContent;
